import { pusherManager, PusherInstanceId } from "utils/pusher";

interface EventCallback {
  id: string;
  callback: (data: any) => void;
}

interface ChannelSubscription {
  channel: any;
  eventCallbacks: Map<string, EventCallback[]>;
  subscriberCount: number;
  pusherInstanceId: PusherInstanceId;
}

class RealtimeConnectionManager {
  private static instance: RealtimeConnectionManager;
  private channels: Map<string, ChannelSubscription> = new Map();
  private callbackCounter = 0;

  private constructor() {
  }

  static getInstance(): RealtimeConnectionManager {
    if (!RealtimeConnectionManager.instance) {
      RealtimeConnectionManager.instance = new RealtimeConnectionManager();
    }
    return RealtimeConnectionManager.instance;
  }


  subscribe(
    channelName: string,
    events: string[],
    callback?: (data: any) => void,
    pusherInstanceId: PusherInstanceId = 'default'
  ): string {
    const callbackId = `cb_${++this.callbackCounter}`;

    if (!this.channels.has(channelName)) {
      this.createChannelSubscription(channelName, pusherInstanceId);
    }

    const subscription = this.channels.get(channelName)!;
    subscription.subscriberCount++;

    events.forEach(event => {
      if (typeof event !== "string" || !event.trim()) {
        console.warn(`Invalid event name: ${event}`);
        return;
      }

      if (!subscription.eventCallbacks.has(event)) {
        subscription.eventCallbacks.set(event, []);
        
        subscription.channel.bind(event, (data: any) => {
          console.log(`Event "${event}" triggered on channel "${channelName}" with data:`, data);

          const callbacks = subscription.eventCallbacks.get(event) || [];
          callbacks.forEach(cb => {
            try {
              cb.callback(data);
            } catch (error) {
              console.error(`Error executing callback for event ${event}:`, error);
            }
          });
        });
      }

      if (callback) {
        subscription.eventCallbacks.get(event)!.push({
          id: callbackId,
          callback
        });
      }
    });

    return callbackId;
  }

  unsubscribe(channelName: string, events: string[], callbackId: string): void {
    const subscription = this.channels.get(channelName);
    if (!subscription) return;

    subscription.subscriberCount--;

    events.forEach(event => {
      const callbacks = subscription.eventCallbacks.get(event);
      if (callbacks) {
        const filteredCallbacks = callbacks.filter(cb => cb.id !== callbackId);
        
        if (filteredCallbacks.length === 0) {
          subscription.channel.unbind(event);
          subscription.eventCallbacks.delete(event);
        } else {
          subscription.eventCallbacks.set(event, filteredCallbacks);
        }
      }
    });

    if (subscription.subscriberCount <= 0 && subscription.eventCallbacks.size === 0) {
      this.cleanupChannel(channelName);
    }
  }

  private createChannelSubscription(channelName: string, pusherInstanceId: PusherInstanceId): void {
    try {
      const pusherInstance = pusherManager.getPusherInstance(pusherInstanceId);
      const channel = pusherInstance.subscribe(channelName);
      this.channels.set(channelName, {
        channel,
        eventCallbacks: new Map(),
        subscriberCount: 0,
        pusherInstanceId
      });
    } catch (error) {
      console.error(`Error creating channel subscription for ${channelName} on pusher instance ${pusherInstanceId}:`, error);
      throw error;
    }
  }

  private cleanupChannel(channelName: string): void {
    try {
      const subscription = this.channels.get(channelName);
      if (subscription) {
        const pusherInstance = pusherManager.getPusherInstance(subscription.pusherInstanceId);
        pusherInstance.unsubscribe(channelName);
        this.channels.delete(channelName);
      }
    } catch (error) {
      console.error(`Error cleaning up channel ${channelName}:`, error);
    }
  }

  getConnectionStats(): Record<string, { events: string[]; subscribers: number; pusherInstance: PusherInstanceId }> {
    const stats: Record<string, { events: string[]; subscribers: number; pusherInstance: PusherInstanceId }> = {};

    this.channels.forEach((subscription, channelName) => {
      stats[channelName] = {
        events: Array.from(subscription.eventCallbacks.keys()),
        subscribers: subscription.subscriberCount,
        pusherInstance: subscription.pusherInstanceId
      };
    });

    return stats;
  }

  cleanup(): void {
    this.channels.forEach((_, channelName) => {
      this.cleanupChannel(channelName);
    });
  }
}

export default RealtimeConnectionManager;